/**
 * Shared filter panel logic hook
 *
 * Extracts all logic shared between FilterSheetContainer (scouting
 * page drawer) and InlineFilterPanelContainer (modal filter panel).
 * Both surfaces use draft filter semantics: changes are local until
 * committed via "Show Results". Provides section toggle state,
 * chip dropdown state, per-section active counts, categorical
 * toggle/clear callbacks, range setters, and action handlers.
 * @module features/scouting/hooks/useFilterPanelLogic
 */

import { useCallback, useMemo, useState } from "react";

import countSectionFilters from "../utils/countSectionFilters";
import type { SectionKey } from "../utils/countSectionFilters";
import toggleId from "@/utils/toggleId";

import useDraftFilterState from "./useDraftFilterState";
import type { FilterState } from "@/hooks/useFilterState";

/** Parameters for the useFilterPanelLogic hook */
interface UseFilterPanelLogicParams {
  /** Committed filter state to draft from and commit to */
  readonly filterState: FilterState;
  /** Callback to close the panel/drawer */
  readonly onClose?: () => void;
  /** IDs of currently available org-level custom metrics for pruning orphaned ranges */
  readonly availableMetricIds?: ReadonlySet<string>;
}

/**
 * Manages all shared filter panel logic: draft state, section toggles,
 * chip dropdowns, per-section counts, toggle/clear/range callbacks,
 * and the cancel/show-results actions.
 * @param params - Hook parameters
 * @param params.filterState - Committed filter state to draft from and commit to
 * @param params.onClose - Callback to close the panel/drawer
 * @param params.availableMetricIds - IDs of currently available org-level custom metrics for pruning orphaned ranges
 * @returns All values needed by both FilterSheetView and InlineFilterPanelView
 */
const useFilterPanelLogic = ({
  filterState,
  onClose,
  availableMetricIds,
}: UseFilterPanelLogicParams) => {
  const {
    draft,
    setDraftField,
    updateDraft,
    activeFilterCount,
    clearDraft,
    clearSportingDraft,
    commit,
    reinitialize,
  } = useDraftFilterState(filterState, availableMetricIds);

  /* --- Section expand/collapse state --- */
  const [expandedSections, setExpandedSections] = useState<
    Record<SectionKey, boolean>
  >({
    basic: false,
    financial: false,
    fitAndRatings: false,
    performance: false,
    goalkeeper: false,
    reportStats: false,
  });

  const toggleSection = useCallback((key: SectionKey) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const onToggleBasic = useCallback(
    () => toggleSection("basic"),
    [toggleSection]
  );
  const onToggleFinancial = useCallback(
    () => toggleSection("financial"),
    [toggleSection]
  );
  const onToggleFitRatings = useCallback(
    () => toggleSection("fitAndRatings"),
    [toggleSection]
  );
  const onTogglePerformance = useCallback(
    () => toggleSection("performance"),
    [toggleSection]
  );
  const onToggleGoalkeeper = useCallback(
    () => toggleSection("goalkeeper"),
    [toggleSection]
  );
  const onToggleReportStats = useCallback(
    () => toggleSection("reportStats"),
    [toggleSection]
  );

  const sectionToggleHandlers = useMemo(
    () => ({
      basic: onToggleBasic,
      financial: onToggleFinancial,
      fitAndRatings: onToggleFitRatings,
      performance: onTogglePerformance,
      goalkeeper: onToggleGoalkeeper,
      reportStats: onToggleReportStats,
    }),
    [
      onToggleBasic,
      onToggleFinancial,
      onToggleFitRatings,
      onTogglePerformance,
      onToggleGoalkeeper,
      onToggleReportStats,
    ]
  );

  /* --- Chip dropdown open/close state --- */
  const [openChipId, setOpenChipId] = useState<string | null>(null);

  const onTogglePositionFilter = useCallback(
    () => setOpenChipId(prev => (prev === "position" ? null : "position")),
    []
  );
  const onToggleClubFilter = useCallback(
    () => setOpenChipId(prev => (prev === "club" ? null : "club")),
    []
  );
  const onToggleNationalityFilter = useCallback(
    () =>
      setOpenChipId(prev => (prev === "nationality" ? null : "nationality")),
    []
  );
  const onToggleLeagueFilter = useCallback(
    () => setOpenChipId(prev => (prev === "league" ? null : "league")),
    []
  );
  const onToggleCompetitionFilter = useCallback(
    () =>
      setOpenChipId(prev => (prev === "competition" ? null : "competition")),
    []
  );
  const onCloseChip = useCallback(() => setOpenChipId(null), []);

  /* --- Per-section active filter counts (from draft) --- */
  const sectionCounts = useMemo(
    () => countSectionFilters(draft, availableMetricIds),
    [draft, availableMetricIds]
  );

  /* --- Toggle callbacks for categorical filters (update draft) --- */
  const handleToggleRole = useCallback(
    (id: string) =>
      updateDraft(prev => ({
        selectedRoleArchetypes: toggleId(prev.selectedRoleArchetypes, id),
      })),
    [updateDraft]
  );
  const handleClearRoles = useCallback(
    () => setDraftField({ selectedRoleArchetypes: [] }),
    [setDraftField]
  );

  const handleTogglePosition = useCallback(
    (id: string) =>
      updateDraft(prev => ({
        selectedPositionIds: toggleId(prev.selectedPositionIds, id),
      })),
    [updateDraft]
  );
  const handleClearPositions = useCallback(
    () => setDraftField({ selectedPositionIds: [] }),
    [setDraftField]
  );

  const handleToggleClub = useCallback(
    (id: string) =>
      updateDraft(prev => ({
        selectedClubIds: toggleId(prev.selectedClubIds, id),
      })),
    [updateDraft]
  );
  const handleClearClubs = useCallback(
    () => setDraftField({ selectedClubIds: [] }),
    [setDraftField]
  );

  const handleToggleNationality = useCallback(
    (id: string) =>
      updateDraft(prev => ({
        selectedNationalityIds: toggleId(prev.selectedNationalityIds, id),
      })),
    [updateDraft]
  );
  const handleClearNationalities = useCallback(
    () => setDraftField({ selectedNationalityIds: [] }),
    [setDraftField]
  );

  const handleToggleLeague = useCallback(
    (id: string) =>
      updateDraft(prev => ({
        selectedLeagueIds: toggleId(prev.selectedLeagueIds, id),
      })),
    [updateDraft]
  );
  const handleClearLeagues = useCallback(
    () => setDraftField({ selectedLeagueIds: [] }),
    [setDraftField]
  );
  const handleToggleCompetition = useCallback(
    (id: string) =>
      updateDraft(prev => ({
        selectedCompetitionIds: toggleId(prev.selectedCompetitionIds, id),
      })),
    [updateDraft]
  );
  const handleClearCompetitions = useCallback(
    () => setDraftField({ selectedCompetitionIds: [] }),
    [setDraftField]
  );
  const handleSetLeagueIds = useCallback(
    (ids: readonly string[]) => setDraftField({ selectedLeagueIds: ids }),
    [setDraftField]
  );

  /* --- Range setter callbacks using setDraftField --- */
  const handleSetAgeRange = useCallback(
    (range: [number, number]) => setDraftField({ ageRange: range }),
    [setDraftField]
  );
  const handleSetContractMonthsRange = useCallback(
    (range: [number, number]) => setDraftField({ contractMonthsRange: range }),
    [setDraftField]
  );
  const handleSetValuationRange = useCallback(
    (range: [number, number]) => setDraftField({ valuationRange: range }),
    [setDraftField]
  );
  const handleSetGprRange = useCallback(
    (range: [number, number]) => setDraftField({ gprRange: range }),
    [setDraftField]
  );
  const handleSetFitScoreRange = useCallback(
    (range: [number, number]) => setDraftField({ fitScoreRange: range }),
    [setDraftField]
  );
  const handleSetTeamStyleFitRange = useCallback(
    (range: [number, number]) => setDraftField({ teamStyleFitRange: range }),
    [setDraftField]
  );
  const handleSetCarryingRange = useCallback(
    (range: [number, number]) => setDraftField({ carryingRange: range }),
    [setDraftField]
  );
  const handleSetDribblingRange = useCallback(
    (range: [number, number]) => setDraftField({ dribblingRange: range }),
    [setDraftField]
  );
  const handleSetPassingRange = useCallback(
    (range: [number, number]) => setDraftField({ passingRange: range }),
    [setDraftField]
  );
  const handleSetShootingRange = useCallback(
    (range: [number, number]) => setDraftField({ shootingRange: range }),
    [setDraftField]
  );
  const handleSetGkPassingRange = useCallback(
    (range: [number, number]) => setDraftField({ gkPassingRange: range }),
    [setDraftField]
  );
  const handleSetGkShotStoppingRange = useCallback(
    (range: [number, number]) => setDraftField({ gkShotStoppingRange: range }),
    [setDraftField]
  );
  const handleSetGkPositioningRange = useCallback(
    (range: [number, number]) => setDraftField({ gkPositioningRange: range }),
    [setDraftField]
  );
  const handleSetCustomStatRange = useCallback(
    (name: string, range: [number, number]) =>
      updateDraft(prev => ({
        customStatRanges: { ...prev.customStatRanges, [name]: range },
      })),
    [updateDraft]
  );

  /* --- Action handlers --- */
  const closePanel = onClose ?? filterState.closeFilterSheet;

  const handleShowResults = useCallback(() => {
    commit();
    setOpenChipId(null);
    closePanel();
  }, [commit, closePanel]);

  const handleCancel = useCallback(() => {
    setOpenChipId(null);
    closePanel();
  }, [closePanel]);

  return {
    /* Draft state */
    draft,
    setDraftField,
    updateDraft,
    activeFilterCount,
    clearDraft,
    clearSportingDraft,
    commit,
    reinitialize,

    /* Section state */
    expandedSections,
    onToggleBasic,
    onToggleFinancial,
    onToggleFitRatings,
    onTogglePerformance,
    onToggleGoalkeeper,
    onToggleReportStats,
    sectionToggleHandlers,

    /* Chip dropdown state */
    openChipId,
    setOpenChipId,
    onTogglePositionFilter,
    onToggleClubFilter,
    onToggleNationalityFilter,
    onToggleLeagueFilter,
    onToggleCompetitionFilter,
    onCloseChip,

    /* Section counts */
    sectionCounts,

    /* Toggle/clear callbacks */
    handleToggleRole,
    handleClearRoles,
    handleTogglePosition,
    handleClearPositions,
    handleToggleClub,
    handleClearClubs,
    handleToggleNationality,
    handleClearNationalities,
    handleToggleLeague,
    handleClearLeagues,
    handleToggleCompetition,
    handleClearCompetitions,
    handleSetLeagueIds,

    /* Range callbacks */
    handleSetAgeRange,
    handleSetContractMonthsRange,
    handleSetValuationRange,
    handleSetGprRange,
    handleSetFitScoreRange,
    handleSetTeamStyleFitRange,
    handleSetCarryingRange,
    handleSetDribblingRange,
    handleSetPassingRange,
    handleSetShootingRange,
    handleSetGkPassingRange,
    handleSetGkShotStoppingRange,
    handleSetGkPositioningRange,
    handleSetCustomStatRange,

    /* Actions */
    handleShowResults,
    handleCancel,
  };
};

export default useFilterPanelLogic;
